import {
  type Afrekening,
  type BookingStatus,
  berekenAfrekening,
  canSettle,
  cents,
  formatEuros,
  formatInBrussels,
  METERS,
  type MeterKind,
} from "@bivak/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { nl } from "../../copy/nl.js";
import {
  formatStand,
  parseAantal,
  parseSchadeEuros,
  parseStand,
  standNaarInvoer,
} from "../../lib/afrekening.js";
import {
  type AfrekeningResponse,
  ApiError,
  fetchAdminBooking,
  fetchAfrekening,
  type NewLezing,
  reopenAfrekening,
  saveLezingen,
  settleBooking,
} from "../../lib/api.js";
import { asCents } from "../../lib/format.js";

const route = getRouteApi("/beheer/boekingen/$boekingId/afrekening");

const INPUT =
  "min-h-11 w-full rounded-lg border-2 border-lijn bg-kaart px-3 font-mono text-[15px] focus:border-dennen focus:outline-none";

type Invoer = Record<MeterKind, { arrival: string; departure: string }>;

function invoerVan(data: AfrekeningResponse): Invoer {
  const invoer = {
    electricity: { arrival: "", departure: "" },
    gas: { arrival: "", departure: "" },
    water: { arrival: "", departure: "" },
  };
  for (const reading of data.readings) {
    invoer[reading.meter][reading.moment] = standNaarInvoer(reading.value);
  }
  return invoer;
}

/** Every parseable reading, for the PUT — partial saves are the normal flow. */
function lezingenVan(invoer: Invoer): NewLezing[] {
  const lezingen: NewLezing[] = [];
  for (const meter of METERS) {
    for (const moment of ["arrival", "departure"] as const) {
      const value = parseStand(invoer[meter][moment]);
      if (value !== undefined && invoer[meter][moment] !== "") {
        lezingen.push({ meter, moment, value });
      }
    }
  }
  return lezingen;
}

/** SCHERM 6 in the sketch: readings in, itemised result out. Mobile-first (NFR-5). */
export function BeheerAfrekeningPage() {
  const { boekingId } = route.useParams();
  const detail = useQuery({
    queryKey: ["admin-booking", boekingId],
    queryFn: () => fetchAdminBooking(boekingId),
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });
  const afrekening = useQuery({
    queryKey: ["afrekening", boekingId],
    queryFn: () => fetchAfrekening(boekingId),
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (detail.isPending || afrekening.isPending) {
    return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  }
  if (detail.error || afrekening.error || !detail.data || !afrekening.data) {
    return <p className="py-16 text-center text-mist">{nl.algemeen.fout}</p>;
  }
  return (
    <Scherm
      boekingId={boekingId}
      naam={detail.data.partyName}
      data={afrekening.data}
      voorstel={voorstelVan(detail.data.headcounts)}
    />
  );
}

type Voorstel = { total: number; under31: number | null } | undefined;

/**
 * Prefill for the actual-attendance fields: the count recorded earlier
 * (re-settle after a reopen), else the contracted headcount, else the booked
 * guess — a starting point to correct, never an answer to accept blindly.
 * under_31 is NULL on booked rows since 2026-08-22 (never claimed at booking);
 * the field then starts empty and the kassier types the real split here.
 */
function voorstelVan(
  headcounts: { kind: string; total: number; under31: number | null }[],
): Voorstel {
  for (const kind of ["actual", "contracted", "booked"]) {
    const headcount = headcounts.find((candidate) => candidate.kind === kind);
    if (headcount) return { total: headcount.total, under31: headcount.under31 };
  }
  return undefined;
}

function Scherm({
  boekingId,
  naam,
  data,
  voorstel,
}: {
  boekingId: string;
  naam: string;
  data: AfrekeningResponse;
  voorstel: Voorstel;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [invoer, setInvoer] = useState<Invoer>(() => invoerVan(data));
  const [schade, setSchade] = useState(() =>
    // Whole euros, matching what parseSchadeEuros accepts back. Damage is only
    // ever entered whole through this screen, so rounding is a no-op guard.
    data.settlement ? String(Math.round(data.settlement.damageCents / 100)) : "",
  );
  // Actual attendance (0.13b): required before settling, because the subsidy
  // is computed from it and cannot be asked of the group years later (§7).
  const [personen, setPersonen] = useState(() => (voorstel ? String(voorstel.total) : ""));
  const [onder31, setOnder31] = useState(() =>
    voorstel?.under31 != null ? String(voorstel.under31) : "",
  );

  const schadeCents = parseSchadeEuros(schade);
  const personenAantal = parseAantal(personen);
  const onder31Aantal = parseAantal(onder31);
  const onder31TeVeel =
    personenAantal !== undefined && onder31Aantal !== undefined && onder31Aantal > personenAantal;
  const aanwezigenOk =
    personenAantal !== undefined &&
    personenAantal >= 1 &&
    onder31Aantal !== undefined &&
    onder31Aantal <= personenAantal;
  const standen = Object.fromEntries(
    METERS.map((meter) => [
      meter,
      {
        arrival: parseStand(invoer[meter].arrival),
        departure: parseStand(invoer[meter].departure),
      },
    ]),
  );
  // The engine runs here in the browser — the SAME code the server settles
  // with, so the preview can never disagree with the stored result.
  const berekend = berekenAfrekening({
    standen,
    prijzen: data.prijzen,
    stayDate: data.arrivalDate,
    depositReceivedCents: cents(data.deposit?.receivedCents ?? 0),
    damageCents: cents(schadeCents ?? 0),
  });

  const bewaar = useMutation({
    mutationFn: () => saveLezingen(boekingId, lezingenVan(invoer)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["afrekening", boekingId] }),
  });
  const rekenAf = useMutation({
    mutationFn: async () => {
      // Save first: the server settles from STORED readings, never from the
      // screen's unsaved keystrokes.
      await saveLezingen(boekingId, lezingenVan(invoer));
      // The button is disabled until every count parses; the ?? 0 fallbacks
      // can only reach the server through a bug, and its validation answers
      // with a Dutch 400 rather than writing a wrong subsidy basis.
      return settleBooking(boekingId, {
        schadeCents: schadeCents ?? 0,
        actualPersonen: personenAantal ?? 0,
        actualOnder31: onder31Aantal ?? 0,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-booking", boekingId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["afrekening", boekingId] });
      void navigate({ to: "/beheer/boekingen/$boekingId", params: { boekingId } });
    },
  });

  const heropen = useMutation({
    mutationFn: () => reopenAfrekening(boekingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["afrekening", boekingId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-booking", boekingId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
    },
  });

  const vast = data.settlement !== null;
  const magAfrekenen =
    !vast &&
    canSettle(data.status as BookingStatus) &&
    berekend.compleet &&
    schadeCents !== undefined &&
    aanwezigenOk;
  const bezig = bewaar.isPending || rekenAf.isPending;
  const fout = rekenAf.error ?? bewaar.error ?? heropen.error;

  return (
    <div className="mx-auto max-w-xl">
      <Link
        to="/beheer/boekingen/$boekingId"
        params={{ boekingId }}
        className="font-mono text-[11px] text-mist uppercase tracking-widest"
      >
        {nl.beheer.afrekening.terugNaarBoeking} /
      </Link>
      <h1 className="mt-1 font-bold font-display text-xl tracking-tight">
        {nl.beheer.afrekening.titel}
      </h1>
      <p className="mb-4 text-[13px] text-mist">
        {naam} · {nl.beheer.afrekening.prijsOpVerblijf(data.arrivalDate)}
      </p>

      {vast && data.settlement && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-zand px-3 py-2">
          <span className="font-semibold text-[12.5px]">
            {nl.beheer.afrekening.afgerekendOp(
              formatInBrussels(new Date(data.settlement.settledAt)),
            )}
          </span>
          <button
            type="button"
            disabled={heropen.isPending}
            onClick={() => {
              // Reopening voids a fixed record — always ask.
              if (window.confirm(nl.beheer.afrekening.heropenZeker)) heropen.mutate();
            }}
            className="min-h-11 font-semibold text-rood text-sm disabled:opacity-50"
          >
            {nl.beheer.afrekening.heropen}
          </button>
        </div>
      )}
      {fout && (
        <p className="mb-4 rounded-lg bg-rood/10 px-3 py-2 font-semibold text-[12.5px] text-rood">
          {fout instanceof ApiError ? fout.message : nl.algemeen.fout}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {METERS.map((meter) => (
          <MeterKaart
            key={meter}
            meter={meter}
            invoer={invoer[meter]}
            vast={vast}
            berekend={berekend}
            onChange={(moment, value) =>
              setInvoer((huidig) => ({
                ...huidig,
                [meter]: { ...huidig[meter], [moment]: value },
              }))
            }
          />
        ))}

        {!vast && (
          <div>
            {/* Deliberately dead: the HomeWizard P1 integration is Phase 2
                (backlog) — the button exists so stakeholders can react to the
                idea at the demo. The tooltip says so honestly. */}
            <button
              type="button"
              disabled
              title={nl.beheer.afrekening.p1Binnenkort}
              className="min-h-11 w-full rounded-lg border-2 border-lijn border-dashed font-semibold text-mist text-sm opacity-70"
            >
              ⚡ {nl.beheer.afrekening.p1Knop}
            </button>
            <p className="mt-1 text-center text-[11.5px] text-mist">
              {nl.beheer.afrekening.p1Binnenkort}
            </p>
          </div>
        )}

        <section className="rounded-xl border border-lijn bg-kaart p-4">
          <h2 className="mb-1 font-bold font-display text-[14.5px]">
            {nl.beheer.afrekening.aanwezigen}
          </h2>
          {/* WHY this is asked here and required: the subsidy is computed from
              ACTUAL attendance, and a group from 2027 cannot be asked in 2029
              (0.13b, §7). */}
          <p className="mb-3 text-[12px] text-mist">{nl.beheer.afrekening.aanwezigenUitleg}</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="block min-w-0">
              <span className="mb-1.5 block font-semibold text-xs">
                {nl.beheer.afrekening.aantalPersonen}
              </span>
              <input
                inputMode="numeric"
                value={personen}
                onChange={(e) => setPersonen(e.target.value)}
                disabled={vast}
                className={
                  !vast && (personenAantal === undefined || personenAantal < 1)
                    ? `${INPUT} border-rood`
                    : INPUT
                }
              />
            </label>
            <label className="block min-w-0">
              <span className="mb-1.5 block font-semibold text-xs">
                {nl.beheer.afrekening.aantalOnder31}
              </span>
              <input
                inputMode="numeric"
                value={onder31}
                onChange={(e) => setOnder31(e.target.value)}
                disabled={vast}
                className={
                  !vast && (onder31Aantal === undefined || onder31TeVeel)
                    ? `${INPUT} border-rood`
                    : INPUT
                }
              />
            </label>
          </div>
          <p
            className={`mt-2 text-[12px] ${!vast && !aanwezigenOk ? "font-semibold text-rood" : "text-mist"}`}
          >
            {vast || aanwezigenOk
              ? nl.beheer.afrekening.onder31Hint
              : onder31TeVeel
                ? nl.beheer.afrekening.onder31TeVeel
                : nl.beheer.afrekening.aanwezigenOnvolledig}
          </p>
        </section>

        <section className="rounded-xl border border-lijn bg-kaart p-4">
          <label className="block">
            <span className="mb-1.5 block font-semibold text-xs">
              {nl.beheer.afrekening.schade} (€)
            </span>
            <input
              inputMode="numeric"
              value={schade}
              onChange={(e) => setSchade(e.target.value)}
              disabled={vast}
              className={schadeCents === undefined ? `${INPUT} border-rood` : INPUT}
            />
            <span className="mt-1.5 block text-[11.5px] text-mist">
              {nl.beheer.afrekening.schadeHint}
            </span>
          </label>
        </section>

        <Totalen berekend={berekend} heeftWaarborg={data.deposit !== null} />

        {!vast && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={bezig || lezingenVan(invoer).length === 0}
              onClick={() => bewaar.mutate()}
              className="min-h-11 rounded-lg border-2 border-lijn font-semibold text-sm disabled:opacity-50"
            >
              {nl.beheer.afrekening.bewaar}
            </button>
            {bewaar.isSuccess && !bewaar.isPending && (
              <p className="text-center text-[12px] text-dennen">{nl.beheer.afrekening.bewaard}</p>
            )}
            <button
              type="button"
              disabled={!magAfrekenen || bezig}
              onClick={() => rekenAf.mutate()}
              className="min-h-11 rounded-lg bg-rood font-semibold text-kaart text-sm disabled:opacity-50"
            >
              {nl.beheer.afrekening.rekenAf}
            </button>
            <p className="text-center text-[11.5px] text-mist">
              {/* Name the ACTUAL blocker: "vul je standen in" was the wrong
                  advice for a departure below the arrival (persona review). */}
              {berekend.compleet
                ? aanwezigenOk
                  ? nl.beheer.afrekening.rekenAfUitleg
                  : onder31TeVeel
                    ? nl.beheer.afrekening.onder31TeVeel
                    : nl.beheer.afrekening.aanwezigenOnvolledig
                : berekend.fouten.some((fout) => fout.reason === "negatief_verbruik")
                  ? nl.beheer.afrekening.negatief
                  : berekend.fouten.length > 0
                    ? nl.beheer.afrekening.geenPrijs
                    : nl.beheer.afrekening.onvolledig}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MeterKaart({
  meter,
  invoer,
  vast,
  berekend,
  onChange,
}: {
  meter: MeterKind;
  invoer: { arrival: string; departure: string };
  vast: boolean;
  berekend: Afrekening;
  onChange: (moment: "arrival" | "departure", value: string) => void;
}) {
  const line = berekend.lines.find((candidate) => candidate.meter === meter);
  const fout = berekend.fouten.find((candidate) => candidate.meter === meter);
  return (
    <section className="rounded-xl border border-lijn bg-kaart p-4">
      <h2 className="mb-3 font-bold font-display text-[14.5px]">
        {nl.beheer.afrekening.meters[meter]}
        {line && (
          <span className="ml-2 font-mono font-normal text-[11px] text-mist">
            {formatEuros(line.unitCents)}/{nl.beheer.afrekening.eenheid(line.unit)}
          </span>
        )}
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {(["arrival", "departure"] as const).map((moment) => (
          <label key={moment} className="block min-w-0">
            <span className="mb-1.5 block font-semibold text-xs">
              {moment === "arrival"
                ? nl.beheer.afrekening.aankomststand
                : nl.beheer.afrekening.vertrekstand}
            </span>
            <input
              inputMode="decimal"
              value={invoer[moment]}
              onChange={(e) => onChange(moment, e.target.value)}
              disabled={vast}
              className={
                invoer[moment] !== "" && parseStand(invoer[moment]) === undefined
                  ? `${INPUT} border-rood`
                  : INPUT
              }
            />
          </label>
        ))}
      </div>
      <p className={`mt-2 text-[12px] ${fout ? "font-semibold text-rood" : "text-mist"}`}>
        {fout
          ? fout.reason === "negatief_verbruik"
            ? nl.beheer.afrekening.negatief
            : nl.beheer.afrekening.geenPrijs
          : line
            ? `${nl.beheer.afrekening.verbruik}: ${formatStand(line.verbruik)} ${nl.beheer.afrekening.eenheid(line.unit)} · ${formatEuros(line.amountCents)}`
            : "…"}
      </p>
    </section>
  );
}

function Totalen({ berekend, heeftWaarborg }: { berekend: Afrekening; heeftWaarborg: boolean }) {
  const saldo = berekend.balanceCents;
  return (
    <section className="rounded-xl border border-lijn bg-kaart p-4">
      <Rij label={nl.beheer.afrekening.verbruik} bedrag={berekend.consumptionCents} />
      <Rij label={nl.beheer.afrekening.schade} bedrag={berekend.damageCents} />
      <Rij
        label={
          heeftWaarborg ? nl.beheer.afrekening.waarborgOntvangen : nl.beheer.afrekening.geenWaarborg
        }
        bedrag={berekend.depositAppliedCents}
      />
      <div className="mt-2 flex items-baseline justify-between border-lijn border-t pt-2.5 font-bold">
        <span>
          {saldo > 0
            ? nl.beheer.afrekening.terugTeStorten
            : saldo < 0
              ? nl.beheer.afrekening.nogTeOntvangen
              : nl.beheer.afrekening.inEvenwicht}
        </span>
        {saldo !== 0 && (
          <span className="font-mono text-[16px]">{formatEuros(asCents(Math.abs(saldo)))}</span>
        )}
      </div>
    </section>
  );
}

function Rij({ label, bedrag }: { label: string; bedrag: number }) {
  return (
    <div className="flex items-baseline justify-between border-lijn border-b border-dashed py-1.5 text-[13px]">
      <span>{label}</span>
      <span className="font-mono">{formatEuros(asCents(bedrag))}</span>
    </div>
  );
}
